import * as React from 'react';
import { GroupedSelectOptions, SelectOption } from '../select';
import { RemoveExcessWhitespace } from '../utils/string-util';
import { useOutsideClick } from '../utils/mouse-util';
import { ConditionalWrapper } from '../utils/apply-wrappers';
import LazyLoad from 'react-lazy-load';
import {
    IconCancelSmall,
    IconCheckSmall,
    IconChevronLeftSmall,
    IconChevronRightSmall,
    IconMagnifierSmall,
} from '../foundation/icons';
import { Checkbox, FormControl, Input, Portal, Typography, Button, useTheme, useDebounce } from '..';
import * as styles from './select-options.styles';
import { cx } from 'linaria';
import { disabledMenuItemRoot } from './select-options.styles';

export interface SelectOptionProps {
    menuItems?: Array<SelectOption>;
    groupedMenuItems?: Array<GroupedSelectOptions>;
    parentRef: React.MutableRefObject<HTMLElement>;
    popperProps: (props?: HTMLElement) => HTMLElement;
    isVisible: boolean;
    setIsVisible: (isVisible: boolean) => void;
    onSelectItem: (selectedValue: SelectOption | GroupedSelectOptions) => void;
    onCreateItem?: (createdValue: SelectOption) => void;
    isSearchable?: boolean;
    useOuterSearch?: boolean;
    outerSearchValue?: string;
    isMultiselect?: boolean;
    isCreatable?: boolean;
    parentBounds?: DOMRect;
    createPlaceholderMessage?: string;
    searchPlaceholderMessage?: string;
    isNestedGroups?: boolean;
    selectedGroup?: GroupedSelectOptions;
    hideSelectedCheckmark?: boolean;
    themeOverride?: 'dark' | 'light';
    withDebounce?: boolean;
}

function renderGroupHeader(
    group: GroupedSelectOptions,
    getSelectedHandler: (menuItem: SelectOption | GroupedSelectOptions) => () => void,
    isNestedGroups: boolean,
    hasSelectedNestGroup: boolean,
) {
    if (isNestedGroups && hasSelectedNestGroup) {
        return (
            <>
                <div
                    key={`group-back-${group.groupName.toLowerCase()}`}
                    data-testid={`group-back-${group.groupName.toLowerCase()}`}
                    className={cx(styles.menuItemRoot)}
                    onClick={getSelectedHandler({ groupName: group.groupName, menuItems: [] } as GroupedSelectOptions)}
                >
                    <div className={cx(styles.menuItemWrapper)}>
                        <IconChevronLeftSmall style={{ color: 'var(--dropdownOptions-icon-color)' }} />
                        <div className={cx(styles.menuItemStyle)}>
                            <div className={cx(styles.menuItemValueStyle)}>
                                <Typography>Change Group</Typography>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    key={`group-label-${group.groupName.toLowerCase()}`}
                    data-testid={`group-label-${group.groupName.toLowerCase()}`}
                    className={cx(styles.groupMenuTitle)}
                >
                    <div className={cx(styles.menuItemWrapper)}>
                        {group.startIcon}
                        <div className={cx(styles.menuItemStyle)}>
                            <div className={cx(styles.menuItemValueStyle)}>
                                <Typography>{group.groupName}</Typography>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    } else if (isNestedGroups && !hasSelectedNestGroup && group.menuItems.length > 0) {
        const selectedItemCount = group.menuItems.filter(
            (item: SelectOption) => item.isSelected && !item.isDisabled,
        ).length;
        return (
            <div
                key={`group-label-${group.groupName.toLowerCase()}`}
                data-testid={`group-label-${group.groupName.toLowerCase()}`}
                className={cx(styles.menuItemRoot)}
                onClick={getSelectedHandler(group)}
            >
                <div className={cx(styles.menuItemWrapper)}>
                    {group.startIcon}
                    <div className={cx(styles.menuItemStyle)}>
                        <div className={cx(styles.menuItemValueStyle)}>
                            <Typography>
                                {group.groupName} {selectedItemCount > 0 ? `(${selectedItemCount} active)` : ''}
                            </Typography>
                        </div>
                    </div>
                </div>
                {group.menuItems.length > 0 && (
                    <IconChevronRightSmall style={{ color: 'var(--dropdownOptions-icon-color)' }} />
                )}
            </div>
        );
    } else if (isNestedGroups && group.menuItems.length === 0) {
        return <span />;
    } else if (RemoveExcessWhitespace(group.groupName) === '') {
        return <span />;
    }

    return (
        <div
            key={`group-label-${group.groupName.toLowerCase()}`}
            data-testid={`group-label-${group.groupName.toLowerCase()}`}
            className={cx(styles.groupMenuTitle)}
        >
            {group.groupName}
        </div>
    );
}

function renderMenuItems(
    optionValues: GroupedSelectOptions[],
    handleOptionSelected: (selectedValue: SelectOption | GroupedSelectOptions) => void,
    isMultiselect: boolean,
    isNestedGroups: boolean,
    hideSelectedCheckmarks: boolean,
) {
    const hasSelectedNestGroup = optionValues.some((value) => value.isSelected);
    const renderItems =
        isNestedGroups && hasSelectedNestGroup ? optionValues.filter((value) => value.isSelected) : optionValues;
    const getSelectedHandler = (menuItem: SelectOption | GroupedSelectOptions) => () => {
        if ('groupName' in menuItem) {
            if (menuItem.menuItems.length === 0) {
                return handleOptionSelected(menuItem); // this clears the selected category to browse back
            }
        } else if (menuItem.onClick && !menuItem.isDisabled) {
            menuItem.onClick();
        }

        return handleOptionSelected(menuItem);
    };

    const MenuItem = React.memo(
        (item: SelectOption) => (
            <div
                key={item.id}
                data-testid={`group-option-${item.id}`}
                className={item.isDisabled ? cx(disabledMenuItemRoot) : cx(styles.menuItemRoot)}
                onClick={item.isDisabled ? undefined : getSelectedHandler(item)}
            >
                <div className={cx(styles.menuItemWrapper)}>
                    {!isMultiselect && item.startIcon ? item.startIcon : null}
                    <div className={item.isDisabled ? cx(styles.disabledMenuItemStyle) : cx(styles.menuItemStyle)}>
                        <div className={cx(styles.menuItemValueStyle)}>
                            {isMultiselect && (
                                <Checkbox
                                    label=""
                                    checked={!item.isDisabled && item.isSelected}
                                    onChange={() => void undefined}
                                />
                            )}
                            {item.value}
                        </div>
                    </div>
                    {!isMultiselect && item.endIcon ? item.endIcon : null}
                </div>
                {!hideSelectedCheckmarks && !item.isDisabled && item.isSelected && !isMultiselect && (
                    <IconCheckSmall className={cx(styles.itemSelected)} />
                )}
            </div>
        ),
        (prevProps, nextProps) => {
            // Checks if the props are different or same, Only re-render if the state of the item changes,
            // It should return true if the old and new props are equal
            return (
                prevProps.isSelected === nextProps.isSelected &&
                prevProps.isDisabled === nextProps.isDisabled &&
                prevProps.value === nextProps.value &&
                prevProps.id === nextProps.id
            );
        },
    );

    return (
        <>
            {renderItems.map((groupedMenuItem: GroupedSelectOptions) => (
                <div key={`group-wrapper-${groupedMenuItem.groupName.toLowerCase()}`}>
                    {renderGroupHeader(groupedMenuItem, getSelectedHandler, isNestedGroups, hasSelectedNestGroup)}
                    {(!isNestedGroups || hasSelectedNestGroup) &&
                        groupedMenuItem.menuItems.map((menuItem: SelectOption, index: number) => {
                            const { id } = menuItem;
                            return (
                                <ConditionalWrapper
                                    key={`conditional-${id}`}
                                    condition={index > 24}
                                    wrapper={(children) => (
                                        <LazyLoad height={52} key={`lazy-load-${id}`}>
                                            {children}
                                        </LazyLoad>
                                    )}
                                >
                                    <MenuItem {...menuItem} />
                                </ConditionalWrapper>
                            );
                        })}
                </div>
            ))}
        </>
    );
}

export const SelectOptions = React.forwardRef<HTMLDivElement, SelectOptionProps>(
    (props: React.PropsWithChildren<SelectOptionProps>, ref) => {
        const {
            menuItems,
            groupedMenuItems = [],
            isVisible,
            setIsVisible,
            popperProps,
            isSearchable,
            useOuterSearch,
            isMultiselect,
            isCreatable: isCreatable,
            onSelectItem,
            onCreateItem,
            outerSearchValue,
            createPlaceholderMessage,
            searchPlaceholderMessage,
            parentRef,
            parentBounds,
            isNestedGroups = false,
            selectedGroup,
            hideSelectedCheckmark,
            themeOverride,
            withDebounce = false,
        } = props;

        const theme = useTheme();

        const debounceSelectCallback = useDebounce((value: any) => {
            onSelectItem(value);
        }, 150);

        // Local state
        const [filter, setFilter] = React.useState('');
        const [cancelIcon, setCancelIcon] = React.useState(false);

        // Hooks
        React.useEffect(() => {
            if (useOuterSearch) {
                setFilter(outerSearchValue ?? '');
            }
        }, [outerSearchValue, menuItems, useOuterSearch]);

        let optionValues: GroupedSelectOptions[] = [];

        if (groupedMenuItems.length > 0) {
            groupedMenuItems.forEach((groupedMenuItem) => {
                optionValues.push({
                    ...groupedMenuItem,
                    isSelected: selectedGroup && selectedGroup.groupName === groupedMenuItem.groupName,
                    menuItems: groupedMenuItem.menuItems.filter(
                        (x: SelectOption) => x.isSelected || x.value.toLowerCase().includes(filter.toLowerCase()),
                    ),
                });
            });
        } else if (menuItems && menuItems.length > 0) {
            optionValues = [
                {
                    groupName: '',
                    menuItems: menuItems.filter((x) =>
                        x.value ? x.value.toLowerCase().includes(filter.toLowerCase()) : 'Invalid',
                    ),
                },
            ];
        }

        const onChangeCapture = (filterValue: string) => {
            setFilter(filterValue);
            setCancelIcon(filterValue.length > 0);
        };

        const icon =
            cancelIcon || (isCreatable && isMultiselect) ? (
                filter && (
                    <IconCancelSmall
                        data-testid="clear-search-button"
                        className={'icon'}
                        style={{ cursor: 'pointer', pointerEvents: 'all' }}
                        onClick={() => onChangeCapture('')}
                    />
                )
            ) : (
                <IconMagnifierSmall className={'icon'} />
            );

        const wrapperRef = React.useRef(null);

        useOutsideClick(
            wrapperRef,
            () => {
                setIsVisible(false);
            },
            parentRef,
        );

        const handleCreateOption = () => {
            onCreateItem &&
                onCreateItem({
                    isSelected: true,
                    value: useOuterSearch ? outerSearchValue : filter,
                    id: menuItems && menuItems.length,
                });
            // Clear input value
            setFilter('');
        };

        /**
         * Return if new item can be created
         */
        const isValidOption = React.useCallback(() => {
            return (
                filter != '' &&
                menuItems &&
                menuItems.filter((option) => option.value.toLowerCase() === filter.toLowerCase()).length === 0
            );
        }, [menuItems, filter]);

        const popper = popperProps();
        popper.style.width = parentBounds && parentBounds.width != 0 ? `${parentBounds.width}px` : 'fit-content';
        popper.style.zIndex = '9999999'; // sets on top of all views

        // should we display search?
        const canSearch =
            (isSearchable && isNestedGroups && selectedGroup) ||
            (!isNestedGroups && isSearchable && !useOuterSearch) ||
            (!isNestedGroups && isCreatable && isMultiselect);

        return (
            <div ref={ref}>
                {isVisible && (
                    <Portal {...popper} data-theme={themeOverride ?? theme}>
                        <div ref={wrapperRef} className={cx(styles.dropDownContainer)}>
                            <div>
                                <div>
                                    {canSearch && !useOuterSearch && (
                                        <div className={cx(styles.searchInputStyle)}>
                                            <FormControl fullWidth>
                                                <Input
                                                    value={filter}
                                                    placeholder={searchPlaceholderMessage ?? 'Search'}
                                                    data-testid="search-box"
                                                    onChange={(event) => onChangeCapture(event.currentTarget.value)}
                                                    endAdornmentSlot={
                                                        <div className={cx(styles.clearContainer)}>{icon}</div>
                                                    }
                                                    fullWidth
                                                />
                                            </FormControl>
                                        </div>
                                    )}
                                    {renderMenuItems(
                                        optionValues,
                                        withDebounce ? debounceSelectCallback : onSelectItem,
                                        isMultiselect ?? false,
                                        isNestedGroups || false,
                                        hideSelectedCheckmark,
                                    )}
                                    {isCreatable && onCreateItem && isValidOption() ? (
                                        <Button
                                            style={{ width: '100%' }}
                                            variant="text"
                                            testId="create-button"
                                            onClick={handleCreateOption}
                                        >
                                            {`${createPlaceholderMessage ?? 'Create'} ${
                                                useOuterSearch ? outerSearchValue : filter
                                            }`}
                                        </Button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </Portal>
                )}
            </div>
        );
    },
);

SelectOptions.displayName = 'SelectOptions';
